import { useMemo, useRef, useState } from "react";
import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery, useQueryClient, useMutation } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import {
  Building2,
  Loader2,
  Plus,
  Trash2,
  Upload,
  Sparkles,
  FileText,
  Download,
  ArrowLeft,
  Palette,
  Image as ImageIcon,
  Mail,
} from "lucide-react";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import {
  adminListClientBrands,
  adminGetClientBrand,
  adminUpsertClientBrand,
  adminDeleteClientBrand,
  adminAttachBrandLogo,
  adminRecordBrandDocument,
  adminGetBrandFileUrl,
  adminDeleteBrandDocument,
  adminGenerateBrandDocument,
  adminPreviewClientBrandDocument,
  adminBulkGenerateBrandDocuments,
  adminExtractBrandContextFromDocs,
  CLIENT_BRAND_BULK_PACK,
  MIN_BUSINESS_DESCRIPTION_CHARS,
} from "@/lib/api/client-brands.functions";

import { BrandExtractionPreview } from "@/components/admin/BrandExtractionPreview";
import { BrandBulkSendDialog } from "@/components/admin/BrandBulkSendDialog";
import { BusinessDescriptionExamples } from "@/components/admin/BusinessDescriptionExamples";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

export const Route = createFileRoute(
  "/_authenticated/portal/admin/client-brands",
)({
  head: () => ({
    meta: [
      { title: "Client brands | Admin" },
      { name: "robots", content: "noindex" },
    ],
  }),
  component: ClientBrandsAdmin,
});

const BUCKET = "client-brands";
const DOC_KINDS = [
  { value: "moi", label: "Memorandum of Incorporation (MOI)" },
  { value: "constitution", label: "Company Constitution" },
  { value: "profile", label: "Company Profile" },
  { value: "management_plan", label: "Management Plan & System" },
  { value: "shareholders_agreement", label: "Shareholders Agreement" },
  { value: "board_charter", label: "Board Charter" },
  { value: "code_of_conduct", label: "Code of Conduct & Ethics" },
  { value: "hr_policy", label: "HR Policy Manual" },
  { value: "popia_policy", label: "POPIA Compliance Policy" },
  { value: "bbbee_policy", label: "B-BBEE Policy" },
  { value: "aml_kyc_policy", label: "AML / KYC Policy" },
  { value: "health_safety_policy", label: "Health & Safety Policy" },
  { value: "business_plan", label: "Business Plan" },
  { value: "nda", label: "Non-Disclosure Agreement" },
  { value: "employment_contract", label: "Employment Contract" },
  { value: "letterhead", label: "Branded Letterhead" },
  { value: "service_agreement", label: "Master Services Agreement" },
  { value: "supplier_code", label: "Supplier Code of Conduct" },
  { value: "esg_policy", label: "ESG & Sustainability Policy" },
  { value: "it_acceptable_use", label: "IT Acceptable Use Policy" },
  { value: "information_security", label: "Information Security Policy" },
  { value: "business_continuity", label: "Business Continuity & DR Plan" },
  { value: "whistleblowing_policy", label: "Whistleblowing Policy" },
  { value: "procurement_policy", label: "Procurement Policy" },
  { value: "conflict_of_interest_policy", label: "Conflict of Interest Policy" },
  { value: "remote_work_policy", label: "Remote Work Policy" },
  { value: "sales_terms", label: "Standard Terms of Sale" },
  { value: "privacy_notice", label: "Website Privacy Notice" },
  { value: "marketing_brochure", label: "Marketing Brochure" },
  { value: "proposal_template", label: "Client Proposal Template" },
];
const DOC_KIND_LABEL: Record<string, string> = Object.fromEntries(
  DOC_KINDS.map((k) => [k.value, k.label]),
);
const UPLOAD_KINDS = [
  { value: "reference", label: "Reference document" },
  { value: "registration", label: "Company registration / CIPC" },
  { value: "tax", label: "Tax / VAT" },
  { value: "bbbee", label: "B-BBEE certificate" },
  { value: "other", label: "Other" },
];

// Upload validation limits
const LOGO_MAX_BYTES = 5 * 1024 * 1024;
const LOGO_ALLOWED_MIME = [
  "image/png",
  "image/jpeg",
  "image/webp",
  "image/svg+xml",
];
const DOC_MAX_BYTES = 25 * 1024 * 1024;
const DOC_ALLOWED_MIME = [
  "application/pdf",
  "application/msword",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  "application/vnd.ms-excel",
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  "image/png",
  "image/jpeg",
  "image/webp",
  "text/plain",
];

function bytesPretty(n: number) {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / 1024 / 1024).toFixed(2)} MB`;
}

function randomRequestId() {
  return `gen_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;
}

type GenLog = { step: string; status: "info" | "ok" | "warn" | "error"; message: string; at: string };
type GenState = {
  kind: string;
  requestId: string;
  status: "running" | "succeeded" | "failed";
  pages?: number;
  error?: string;
  logs: GenLog[];
  startedAt: number;
};

type BulkItem = {
  kind: string;
  status: "queued" | "running" | "succeeded" | "failed";
  pages?: number;
  error?: string;
  startedAt?: number;
  finishedAt?: number;
};



function ClientBrandsAdmin() {
  const qc = useQueryClient();
  const list = useServerFn(adminListClientBrands);
  const upsert = useServerFn(adminUpsertClientBrand);
  const del = useServerFn(adminDeleteClientBrand);

  const { data, isLoading } = useQuery({
    queryKey: ["admin", "client-brands"],
    queryFn: () => list({}),
  });

  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({
    name: "",
    primary_color: "#0F1B3D",
    secondary_color: "#B08D57",
    contact_email: "",
    contact_phone: "",
    website: "",
    address: "",
    registration_no: "",
    vat_no: "",
    industry: "",
    business_description: "",
    brand_voice: "",
    notes: "",
    override_activities: [] as string[],
    override_industries: [] as string[],
    override_service_scope: [] as string[],
    override_regulatory_context: [] as string[],
    override_risk_profile: [] as string[],
  });

  const createMut = useMutation({
    mutationFn: () => upsert({ data: form }),
    onSuccess: ({ id }) => {
      toast.success("Brand created");
      setCreating(false);
      setForm((f) => ({ ...f, name: "" }));
      qc.invalidateQueries({ queryKey: ["admin", "client-brands"] });
      setSelectedId(id);
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const deleteMut = useMutation({
    mutationFn: (id: string) => del({ data: { id } }),
    onSuccess: () => {
      toast.success("Brand removed");
      setSelectedId(null);
      qc.invalidateQueries({ queryKey: ["admin", "client-brands"] });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  if (selectedId) {
    return (
      <ClientBrandDetail
        id={selectedId}
        onBack={() => setSelectedId(null)}
        onDelete={() => deleteMut.mutate(selectedId)}
      />
    );
  }

  return (
    <div className="space-y-6 p-4 md:p-6">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-3">
          <Building2 className="size-7 text-amber-600" />
          <div>
            <h1 className="text-2xl font-bold">Client brands</h1>
            <p className="text-sm text-muted-foreground">
              Create branded company packs (MOI, Constitution, Profile, Letterhead) for external clients.
            </p>
          </div>
        </div>
        <Button onClick={() => setCreating((s) => !s)}>
          <Plus className="size-4 mr-2" /> New brand
        </Button>
      </div>

      {creating && (
        <div className="rounded-lg border bg-card p-4 space-y-3">
          <h2 className="font-semibold">Create a new client brand</h2>
          <div className="grid sm:grid-cols-2 gap-3">
            <Field label="Brand name *">
              <Input
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="Acme Holdings (Pty) Ltd"
              />
            </Field>
            <Field label="Industry">
              <Input
                value={form.industry}
                onChange={(e) =>
                  setForm({ ...form, industry: e.target.value })
                }
              />
            </Field>
            <Field label="Primary colour">
              <Input
                type="color"
                value={form.primary_color}
                onChange={(e) =>
                  setForm({ ...form, primary_color: e.target.value })
                }
              />
            </Field>
            <Field label="Accent colour">
              <Input
                type="color"
                value={form.secondary_color}
                onChange={(e) =>
                  setForm({ ...form, secondary_color: e.target.value })
                }
              />
            </Field>
            <Field label="Email">
              <Input
                type="email"
                value={form.contact_email}
                onChange={(e) =>
                  setForm({ ...form, contact_email: e.target.value })
                }
              />
            </Field>
            <Field label="Phone">
              <Input
                value={form.contact_phone}
                onChange={(e) =>
                  setForm({ ...form, contact_phone: e.target.value })
                }
              />
            </Field>
            <Field label="Website">
              <Input
                value={form.website}
                onChange={(e) => setForm({ ...form, website: e.target.value })}
                placeholder="https://"
              />
            </Field>
            <Field label="Registration no.">
              <Input
                value={form.registration_no}
                onChange={(e) =>
                  setForm({ ...form, registration_no: e.target.value })
                }
              />
            </Field>
            <Field label="VAT no.">
              <Input
                value={form.vat_no}
                onChange={(e) => setForm({ ...form, vat_no: e.target.value })}
              />
            </Field>
            <Field label="Physical address" className="sm:col-span-2">
              <Input
                value={form.address}
                onChange={(e) => setForm({ ...form, address: e.target.value })}
              />
            </Field>
            <Field label="What the company does" className="sm:col-span-2">
              <Textarea
                rows={5}
                value={form.business_description}
                onChange={(e) =>
                  setForm({ ...form, business_description: e.target.value })
                }
                placeholder="e.g. We are a carpentry workshop building kitchens, cupboards and custom furniture for homes and small businesses in Pretoria — OR — an interior-design studio offering space planning, finishes and project supervision. Be specific about trade, products, clients, locations and any licences."
              />
              <BusinessDescriptionExamples
                value={form.business_description}
                onChange={(v) => setForm({ ...form, business_description: v })}
              />
              {(() => {
                const len = form.business_description.trim().length;
                const ok = len >= MIN_BUSINESS_DESCRIPTION_CHARS;
                return (
                  <p
                    className={`mt-1 text-[11px] ${ok ? "text-muted-foreground" : "text-amber-600"}`}
                  >
                    {ok
                      ? `Looks good — ${len} characters. Aim for 3–6 specific sentences for best results.`
                      : `Required for document generation — minimum ${MIN_BUSINESS_DESCRIPTION_CHARS} characters (currently ${len}). Describe activities, industries served, products/services and regulatory context.`}
                  </p>
                );
              })()}
              <BrandExtractionPreview
                businessDescription={form.business_description}
                name={form.name}
                industry={form.industry}
                overrides={{
                  override_activities: form.override_activities,
                  override_industries: form.override_industries,
                  override_service_scope: form.override_service_scope,
                  override_regulatory_context: form.override_regulatory_context,
                  override_risk_profile: form.override_risk_profile,
                }}
                onOverridesChange={(o) => setForm({ ...form, ...o })}
              />
            </Field>
            <Field label="Brand voice / tone" className="sm:col-span-2">
              <Textarea
                rows={2}
                value={form.brand_voice}
                onChange={(e) =>
                  setForm({ ...form, brand_voice: e.target.value })
                }
                placeholder="e.g. confident, modern, formal, client-focused"
              />
            </Field>
            <Field label="Internal notes" className="sm:col-span-2">
              <Textarea
                rows={2}
                value={form.notes}
                onChange={(e) => setForm({ ...form, notes: e.target.value })}
              />
            </Field>
          </div>
          <div className="flex gap-2 justify-end">
            <Button variant="outline" onClick={() => setCreating(false)}>
              Cancel
            </Button>
            <Button
              disabled={!form.name.trim() || createMut.isPending}
              onClick={() => createMut.mutate()}
            >
              {createMut.isPending && (
                <Loader2 className="size-4 mr-2 animate-spin" />
              )}
              Create brand
            </Button>
          </div>
        </div>
      )}

      <div className="rounded-lg border bg-card overflow-hidden">
        {isLoading ? (
          <div className="p-8 text-center text-muted-foreground flex items-center justify-center gap-2">
            <Loader2 className="size-4 animate-spin" /> Loading brands…
          </div>
        ) : (data?.brands ?? []).length === 0 ? (
          <div className="p-8 text-center text-muted-foreground">
            No client brands yet. Create one to get started.
          </div>
        ) : (
          <ul className="divide-y">
            {data!.brands.map((b: any) => (
              <li
                key={b.id}
                className="p-4 flex items-center justify-between gap-3 hover:bg-muted/40 cursor-pointer"
                onClick={() => setSelectedId(b.id)}
              >
                <div className="flex items-center gap-3 min-w-0">
                  <div
                    className="size-10 rounded shrink-0 border"
                    style={{
                      background: `linear-gradient(135deg, ${b.primary_color}, ${b.secondary_color})`,
                    }}
                  />
                  <div className="min-w-0">
                    <div className="font-semibold truncate">{b.name}</div>
                    <div className="text-xs text-muted-foreground truncate">
                      {[b.industry, b.contact_email, b.website]
                        .filter(Boolean)
                        .join(" · ")}
                    </div>
                  </div>
                </div>
                <Button variant="ghost" size="sm">
                  Open →
                </Button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

function Field({
  label,
  children,
  className = "",
}: {
  label: string;
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <div className={className}>
      <Label className="text-xs">{label}</Label>
      <div className="mt-1">{children}</div>
    </div>
  );
}

// ============================================================================
// Detail view
// ============================================================================

function ClientBrandDetail({
  id,
  onBack,
  onDelete,
}: {
  id: string;
  onBack: () => void;
  onDelete: () => void;
}) {
  const qc = useQueryClient();
  const get = useServerFn(adminGetClientBrand);
  const upsert = useServerFn(adminUpsertClientBrand);
  const attachLogo = useServerFn(adminAttachBrandLogo);
  const recordDoc = useServerFn(adminRecordBrandDocument);
  const fileUrl = useServerFn(adminGetBrandFileUrl);
  const deleteDoc = useServerFn(adminDeleteBrandDocument);
  const generate = useServerFn(adminGenerateBrandDocument);
  const previewFn = useServerFn(adminPreviewClientBrandDocument);
  const bulkGenerate = useServerFn(adminBulkGenerateBrandDocuments);
  void bulkGenerate; // kept available; bulk uses per-item sequencing via `generate`.
  const extractFromDocs = useServerFn(adminExtractBrandContextFromDocs);




  const { data, isLoading } = useQuery({
    queryKey: ["admin", "client-brand", id],
    queryFn: () => get({ data: { id } }),
  });

  const brand = data?.brand;

  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState<any>(null);
  const [genKind, setGenKind] = useState<string>("profile");
  const [genExtra, setGenExtra] = useState("");
  const [genTemplate, setGenTemplate] = useState<string>("auto");
  const [uploadKind, setUploadKind] = useState("reference");
  const [uploadTitle, setUploadTitle] = useState("");
  const [busy, setBusy] = useState<string | null>(null);
  const [lastGen, setLastGen] = useState<GenState | null>(null);
  const [bulkItems, setBulkItems] = useState<BulkItem[] | null>(null);
  const [bulkRunning, setBulkRunning] = useState(false);
  const [bulkSendOpen, setBulkSendOpen] = useState(false);
  const [galleryKind, setGalleryKind] = useState<string>("all");
  const [previewState, setPreviewState] = useState<null | {
    pdf_base64: string;
    pages: number;
    layout_report: {
      ok: boolean;
      layout: { font_family: string; font_size_pt: number; alignment: string };
      section_count: number;
      estimated_pages: number;
      warnings: { section: string; kind: string; detail: string }[];
    };
    kind: string;
  }>(null);
  const [previewBusy, setPreviewBusy] = useState(false);
  const logoInputRef = useRef<HTMLInputElement | null>(null);
  const bulkInputRef = useRef<HTMLInputElement | null>(null);
  const [bulkUploading, setBulkUploading] = useState(false);
  const [bulkUploadMsg, setBulkUploadMsg] = useState<string | null>(null);
  const [extracting, setExtracting] = useState(false);
  const [extractionResult, setExtractionResult] = useState<any>(null);



  // hydrate form when data loads
  useMemo(() => {
    if (brand && !form) {
      setForm({
        id: brand.id,
        name: brand.name ?? "",
        primary_color: brand.primary_color ?? "#0F1B3D",
        secondary_color: brand.secondary_color ?? "#B08D57",
        contact_email: brand.contact_email ?? "",
        contact_phone: brand.contact_phone ?? "",
        website: brand.website ?? "",
        address: brand.address ?? "",
        registration_no: brand.registration_no ?? "",
        vat_no: brand.vat_no ?? "",
        industry: brand.industry ?? "",
        business_description: (brand as any).business_description ?? "",
        brand_voice: brand.brand_voice ?? "",
        notes: brand.notes ?? "",
        override_activities: ((brand as any).override_activities ?? []) as string[],
        override_industries: ((brand as any).override_industries ?? []) as string[],
        override_service_scope: ((brand as any).override_service_scope ?? []) as string[],
        override_regulatory_context: ((brand as any).override_regulatory_context ?? []) as string[],
        override_risk_profile: ((brand as any).override_risk_profile ?? []) as string[],
      });
    }
  }, [brand, form]);

  const saveMut = useMutation({
    mutationFn: () => upsert({ data: form }),
    onSuccess: () => {
      toast.success("Brand updated");
      setEditing(false);
      qc.invalidateQueries({ queryKey: ["admin", "client-brand", id] });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const generateMut = useMutation({
    mutationFn: async (opts: { retryRequestId?: string } = {}) => {
      const requestId = opts.retryRequestId ?? randomRequestId();
      setLastGen({
        kind: genKind,
        requestId,
        status: "running",
        logs: [
          { step: "submit", status: "info", message: `Submitting ${DOC_KIND_LABEL[genKind] ?? genKind}…`, at: new Date().toISOString() },
        ],
        startedAt: Date.now(),
      });
      try {
        const r = await generate({
          data: {
            brand_id: id,
            kind: genKind as any,
            extra_instructions: genExtra || undefined,
            request_id: requestId,
            template_override:
              genTemplate === "auto" ? undefined : (genTemplate as any),
          },
        });
        setLastGen((s) =>
          s
            ? { ...s, status: "succeeded", pages: r.pages, logs: [...s.logs, ...(r.logs ?? [])] }
            : s,
        );
        return r;
      } catch (e: any) {
        setLastGen((s) =>
          s
            ? {
                ...s,
                status: "failed",
                error: e?.message ?? "Generation failed",
                logs: [
                  ...s.logs,
                  { step: "error", status: "error", message: e?.message ?? "Generation failed", at: new Date().toISOString() },
                ],
              }
            : s,
        );
        throw e;
      }
    },
    onSuccess: (r) => {
      toast.success(`Generated ${DOC_KIND_LABEL[genKind] ?? genKind} (${r.pages} pages)`);
      setGenExtra("");
      qc.invalidateQueries({ queryKey: ["admin", "client-brand", id] });
    },
    onError: (e: Error) => toast.error(e.message ?? "Generation failed"),
  });

  async function runBulkPack() {
    if (bulkRunning) return;
    setBulkRunning(true);
    const initial: BulkItem[] = CLIENT_BRAND_BULK_PACK.map((k) => ({
      kind: k,
      status: "queued",
    }));
    setBulkItems(initial);
    let okCount = 0;
    let failCount = 0;
    for (let i = 0; i < CLIENT_BRAND_BULK_PACK.length; i++) {
      const kind = CLIENT_BRAND_BULK_PACK[i];
      setBulkItems((prev) =>
        prev
          ? prev.map((it, idx) =>
              idx === i ? { ...it, status: "running", startedAt: Date.now() } : it,
            )
          : prev,
      );
      try {
        const r = await generate({
          data: {
            brand_id: id,
            kind: kind as any,
            request_id: randomRequestId(),
          },
        });
        okCount++;
        setBulkItems((prev) =>
          prev
            ? prev.map((it, idx) =>
                idx === i
                  ? { ...it, status: "succeeded", pages: r.pages, finishedAt: Date.now() }
                  : it,
              )
            : prev,
        );
      } catch (e: any) {
        failCount++;
        setBulkItems((prev) =>
          prev
            ? prev.map((it, idx) =>
                idx === i
                  ? {
                      ...it,
                      status: "failed",
                      error: e?.message ?? "Generation failed",
                      finishedAt: Date.now(),
                    }
                  : it,
              )
            : prev,
        );
        // continue with remaining documents
      }
      qc.invalidateQueries({ queryKey: ["admin", "client-brand", id] });
    }
    setBulkRunning(false);
    if (failCount === 0) toast.success(`Generated full pack: ${okCount} documents`);
    else toast.warning(`Pack done: ${okCount} succeeded, ${failCount} failed`);
  }

  // Single-kind retry inside the bulk progress panel.
  async function retryBulkItem(index: number) {
    const item = bulkItems?.[index];
    if (!item || bulkRunning) return;
    setBulkItems((prev) =>
      prev
        ? prev.map((it, idx) =>
            idx === index ? { ...it, status: "running", error: undefined, startedAt: Date.now() } : it,
          )
        : prev,
    );
    try {
      const r = await generate({
        data: { brand_id: id, kind: item.kind as any, request_id: randomRequestId() },
      });
      setBulkItems((prev) =>
        prev
          ? prev.map((it, idx) =>
              idx === index
                ? { ...it, status: "succeeded", pages: r.pages, finishedAt: Date.now() }
                : it,
            )
          : prev,
      );
      qc.invalidateQueries({ queryKey: ["admin", "client-brand", id] });
    } catch (e: any) {
      setBulkItems((prev) =>
        prev
          ? prev.map((it, idx) =>
              idx === index
                ? { ...it, status: "failed", error: e?.message ?? "Failed", finishedAt: Date.now() }
                : it,
            )
          : prev,
      );
    }
  }



  const deleteDocMut = useMutation({
    mutationFn: (docId: string) => deleteDoc({ data: { id: docId } }),
    onSuccess: () => {
      toast.success("Document removed");
      qc.invalidateQueries({ queryKey: ["admin", "client-brand", id] });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  function validateLogo(file: File): string | null {
    if (!LOGO_ALLOWED_MIME.includes(file.type)) {
      return `Logo type "${file.type || "unknown"}" not allowed. Use PNG, JPEG, WebP or SVG.`;
    }
    if (file.size > LOGO_MAX_BYTES) {
      return `Logo is ${bytesPretty(file.size)}; the maximum is ${bytesPretty(LOGO_MAX_BYTES)}.`;
    }
    if (file.size === 0) return "Logo file is empty.";
    return null;
  }

  function validateRefDoc(file: File): string | null {
    if (file.size === 0) return "File is empty.";
    if (file.size > DOC_MAX_BYTES) {
      return `File is ${bytesPretty(file.size)}; the maximum is ${bytesPretty(DOC_MAX_BYTES)}.`;
    }
    const okExt = /\.(pdf|docx?|xlsx?|png|jpe?g|webp|txt)$/i.test(file.name);
    if (!DOC_ALLOWED_MIME.includes(file.type) && !okExt) {
      return `File type "${file.type || file.name.split(".").pop()}" is not supported. Allowed: PDF, DOC, DOCX, XLS, XLSX, PNG, JPG, WebP, TXT.`;
    }
    return null;
  }

  async function handleLogoUpload(file: File) {
    if (!brand) return;
    const err = validateLogo(file);
    if (err) {
      toast.error(err);
      return;
    }
    setBusy("logo");
    try {
      const ext = (file.name.split(".").pop() ?? "png").toLowerCase().replace(/[^a-z0-9]/g, "") || "png";
      const path = `brand/${id}/logo.${ext}`;
      const { error } = await supabase.storage
        .from(BUCKET)
        .upload(path, file, { upsert: true, contentType: file.type });
      if (error) throw error;
      await attachLogo({ data: { brand_id: id, storage_path: path } });
      toast.success("Logo updated");
      qc.invalidateQueries({ queryKey: ["admin", "client-brand", id] });
    } catch (e: any) {
      toast.error(e?.message ?? "Logo upload failed");
    } finally {
      setBusy(null);
    }
  }

  async function handleDocUpload(file: File) {
    if (!brand) return;
    if (!uploadTitle.trim()) {
      toast.error("Add a title for the document before choosing a file.");
      return;
    }
    const err = validateRefDoc(file);
    if (err) {
      toast.error(err);
      return;
    }
    setBusy("doc");
    try {
      const safeName = file.name.replace(/[^a-zA-Z0-9._-]+/g, "_");
      const path = `brand/${id}/uploads/${Date.now()}-${safeName}`;
      const { error } = await supabase.storage
        .from(BUCKET)
        .upload(path, file, { contentType: file.type });
      if (error) throw error;
      await recordDoc({
        data: {
          brand_id: id,
          kind: uploadKind,
          title: uploadTitle.trim(),
          storage_path: path,
          original_filename: file.name,
          size_bytes: file.size,
          mime_type: file.type,
        },
      });
      toast.success("Document uploaded");
      setUploadTitle("");
      qc.invalidateQueries({ queryKey: ["admin", "client-brand", id] });
    } catch (e: any) {
      toast.error(e?.message ?? "Upload failed");
    } finally {
      setBusy(null);
    }
  }

  async function handleBulkContextUpload(files: FileList | null) {
    if (!brand || !files || files.length === 0) return;
    const list = Array.from(files).slice(0, 20);
    setBulkUploading(true);
    setBulkUploadMsg(null);
    const uploadedIds: string[] = [];
    try {
      for (const file of list) {
        const err = validateRefDoc(file);
        if (err) { toast.error(`${file.name}: ${err}`); continue; }
        const safeName = file.name.replace(/[^a-zA-Z0-9._-]+/g, "_");
        const path = `brand/${id}/context/${Date.now()}-${safeName}`;
        const { error } = await supabase.storage
          .from(BUCKET)
          .upload(path, file, { contentType: file.type });
        if (error) { toast.error(`${file.name}: ${error.message}`); continue; }
        const rec: any = await recordDoc({
          data: {
            brand_id: id,
            kind: "reference",
            title: file.name.replace(/\.[^.]+$/, "").slice(0, 160),
            storage_path: path,
            original_filename: file.name,
            size_bytes: file.size,
            mime_type: file.type,
          },
        });
        if (rec?.id) uploadedIds.push(rec.id);
      }
      qc.invalidateQueries({ queryKey: ["admin", "client-brand", id] });
      // Refetch to get the new document ids if server didn't return them
      const refreshed = await get({ data: { id } });
      const contextDocs = (refreshed?.documents || [])
        .filter((d: any) => d.storage_path?.includes("/context/") && !d.ai_generated)
        .slice(0, 20)
        .map((d: any) => d.id);
      if (contextDocs.length === 0) {
        setBulkUploadMsg("Uploaded — no readable context docs found.");
        return;
      }
      setBulkUploadMsg(`Uploaded ${contextDocs.length} doc(s). Running AI extraction…`);
      setExtracting(true);
      const result = await extractFromDocs({
        data: { brand_id: id, document_ids: contextDocs, apply_description: true },
      });
      setExtractionResult(result);
      setBulkUploadMsg(
        `Extraction complete: ${result.documents_scanned} document(s) scanned.${result.description_applied ? " Business description updated." : ""}`,
      );
      toast.success("AI extraction complete — signals updated");
      qc.invalidateQueries({ queryKey: ["admin", "client-brand", id] });
    } catch (e: any) {
      toast.error(e?.message ?? "Bulk upload failed");
      setBulkUploadMsg(e?.message ?? "Bulk upload failed");
    } finally {
      setBulkUploading(false);
      setExtracting(false);
      if (bulkInputRef.current) bulkInputRef.current.value = "";
    }
  }



  async function openFile(path: string, name?: string) {
    try {
      const r = await fileUrl({
        data: { storage_path: path, download: true, filename: name },
      });
      window.open(r.url, "_blank", "noopener");
    } catch (e: any) {
      toast.error(e?.message ?? "Could not open file");
    }
  }

  if (isLoading || !brand || !form) {
    return (
      <div className="p-8 text-center text-muted-foreground flex items-center justify-center gap-2">
        <Loader2 className="size-4 animate-spin" /> Loading brand…
      </div>
    );
  }

  return (
    <div className="space-y-6 p-4 md:p-6">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="sm" onClick={onBack}>
            <ArrowLeft className="size-4 mr-1" /> Back
          </Button>
          <div>
            <h1 className="text-2xl font-bold">{brand.name}</h1>
            <p className="text-sm text-muted-foreground">
              {brand.industry || "Client brand"} · created{" "}
              {new Date(brand.created_at).toLocaleDateString()}
            </p>
          </div>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => setEditing((s) => !s)}>
            {editing ? "Cancel edit" : "Edit details"}
          </Button>
          <Button
            variant="destructive"
            onClick={() => {
              if (confirm("Delete this brand and all its documents?")) onDelete();
            }}
          >
            <Trash2 className="size-4 mr-1" /> Delete
          </Button>
        </div>
      </div>

      {/* Brand summary / editor */}
      <div className="rounded-lg border bg-card p-4 grid md:grid-cols-3 gap-4">
        <div className="space-y-3">
          <div className="flex items-center gap-2 text-sm font-semibold">
            <ImageIcon className="size-4" /> Logo
          </div>
          <div
            className="aspect-square w-full rounded-md border-2 border-dashed bg-muted/30 flex items-center justify-center overflow-hidden cursor-pointer hover:border-primary transition"
            onClick={() => logoInputRef.current?.click()}
            onDragOver={(e) => {
              e.preventDefault();
              e.currentTarget.classList.add("border-primary", "bg-primary/5");
            }}
            onDragLeave={(e) => {
              e.currentTarget.classList.remove("border-primary", "bg-primary/5");
            }}
            onDrop={(e) => {
              e.preventDefault();
              e.currentTarget.classList.remove("border-primary", "bg-primary/5");
              const f = e.dataTransfer.files?.[0];
              if (f) handleLogoUpload(f);
            }}
            role="button"
            tabIndex={0}
            aria-label="Upload brand logo"
          >
            {busy === "logo" ? (
              <div className="flex flex-col items-center gap-2 text-xs text-muted-foreground">
                <Loader2 className="size-5 animate-spin" /> Uploading…
              </div>
            ) : data?.logoUrl ? (
              <img
                src={data.logoUrl}
                alt={brand.name}
                className="max-w-full max-h-full object-contain"
              />
            ) : (
              <div className="text-xs text-muted-foreground p-4 text-center">
                <Upload className="size-5 mx-auto mb-2" />
                Click or drop a PNG, JPG, WebP or SVG (max {bytesPretty(LOGO_MAX_BYTES)}).
              </div>
            )}
          </div>
          <input
            ref={logoInputRef}
            type="file"
            accept="image/png,image/jpeg,image/webp,image/svg+xml,.png,.jpg,.jpeg,.webp,.svg"
            className="hidden"
            onChange={(e) => {
              const f = e.target.files?.[0];
              if (f) handleLogoUpload(f);
              e.target.value = "";
            }}
          />
          <Button
            size="sm"
            variant="outline"
            className="w-full"
            disabled={busy === "logo"}
            onClick={() => logoInputRef.current?.click()}
          >
            {busy === "logo" ? (
              <Loader2 className="size-4 mr-2 animate-spin" />
            ) : (
              <Upload className="size-4 mr-2" />
            )}
            {data?.logoUrl ? "Replace logo" : "Upload logo"}
          </Button>
        </div>


        <div className="md:col-span-2 space-y-3">
          <div className="flex items-center gap-2 text-sm font-semibold">
            <Palette className="size-4" /> Brand details
          </div>
          {editing ? (
            <>
              <div className="grid sm:grid-cols-2 gap-3">
                <Field label="Brand name">
                  <Input
                    value={form.name}
                    onChange={(e) =>
                      setForm({ ...form, name: e.target.value })
                    }
                  />
                </Field>
                <Field label="Industry">
                  <Input
                    value={form.industry}
                    onChange={(e) =>
                      setForm({ ...form, industry: e.target.value })
                    }
                  />
                </Field>
                <Field label="Primary colour">
                  <Input
                    type="color"
                    value={form.primary_color}
                    onChange={(e) =>
                      setForm({ ...form, primary_color: e.target.value })
                    }
                  />
                </Field>
                <Field label="Accent colour">
                  <Input
                    type="color"
                    value={form.secondary_color}
                    onChange={(e) =>
                      setForm({ ...form, secondary_color: e.target.value })
                    }
                  />
                </Field>
                <Field label="Email">
                  <Input
                    value={form.contact_email}
                    onChange={(e) =>
                      setForm({ ...form, contact_email: e.target.value })
                    }
                  />
                </Field>
                <Field label="Phone">
                  <Input
                    value={form.contact_phone}
                    onChange={(e) =>
                      setForm({ ...form, contact_phone: e.target.value })
                    }
                  />
                </Field>
                <Field label="Website">
                  <Input
                    value={form.website}
                    onChange={(e) =>
                      setForm({ ...form, website: e.target.value })
                    }
                  />
                </Field>
                <Field label="Registration no.">
                  <Input
                    value={form.registration_no}
                    onChange={(e) =>
                      setForm({ ...form, registration_no: e.target.value })
                    }
                  />
                </Field>
                <Field label="VAT no.">
                  <Input
                    value={form.vat_no}
                    onChange={(e) =>
                      setForm({ ...form, vat_no: e.target.value })
                    }
                  />
                </Field>
                <Field label="Address" className="sm:col-span-2">
                  <Input
                    value={form.address}
                    onChange={(e) =>
                      setForm({ ...form, address: e.target.value })
                    }
                  />
                </Field>
                <Field label="What the company does" className="sm:col-span-2">
                  <Textarea
                    rows={5}
                    value={form.business_description}
                    onChange={(e) =>
                      setForm({ ...form, business_description: e.target.value })
                    }
                    placeholder="e.g. carpentry workshop, interior-design studio, plumbing contractor, catering company — be specific about trade, products, clients, locations and any licences."
                  />
                  <BusinessDescriptionExamples
                    value={form.business_description}
                    onChange={(v) => setForm({ ...form, business_description: v })}
                  />
                  {(() => {
                    const len = form.business_description.trim().length;
                    const ok = len >= MIN_BUSINESS_DESCRIPTION_CHARS;
                    return (
                      <p
                        className={`mt-1 text-[11px] ${ok ? "text-muted-foreground" : "text-amber-600"}`}
                      >
                        {ok
                          ? `Looks good — ${len} characters. Aim for 3–6 specific sentences for best results.`
                          : `Required for document generation — minimum ${MIN_BUSINESS_DESCRIPTION_CHARS} characters (currently ${len}). Describe activities, industries served, products/services and regulatory context.`}
                      </p>
                    );
                  })()}
                  <BrandExtractionPreview
                    businessDescription={form.business_description}
                    name={form.name}
                    industry={form.industry}
                    brandId={form.id}
                    overrides={{
                      override_activities: form.override_activities ?? [],
                      override_industries: form.override_industries ?? [],
                      override_service_scope: form.override_service_scope ?? [],
                      override_regulatory_context: form.override_regulatory_context ?? [],
                      override_risk_profile: form.override_risk_profile ?? [],
                    }}
                    onOverridesChange={(o) => setForm({ ...form, ...o })}
                  />
                </Field>
                <Field label="Brand voice" className="sm:col-span-2">
                  <Textarea
                    rows={2}
                    value={form.brand_voice}
                    onChange={(e) =>
                      setForm({ ...form, brand_voice: e.target.value })
                    }
                  />
                </Field>
                <Field label="Internal notes" className="sm:col-span-2">
                  <Textarea
                    rows={2}
                    value={form.notes}
                    onChange={(e) =>
                      setForm({ ...form, notes: e.target.value })
                    }
                  />
                </Field>
              </div>
              <div className="flex justify-end gap-2">
                <Button
                  onClick={() => saveMut.mutate()}
                  disabled={saveMut.isPending}
                >
                  {saveMut.isPending && (
                    <Loader2 className="size-4 mr-2 animate-spin" />
                  )}
                  Save changes
                </Button>
              </div>
            </>
          ) : (
            <>
              <div className="grid sm:grid-cols-2 gap-x-6 gap-y-2 text-sm">
                <ReadRow label="Email" value={brand.contact_email} />
                <ReadRow label="Phone" value={brand.contact_phone} />
                <ReadRow label="Website" value={brand.website} />
                <ReadRow label="Address" value={brand.address} />
                <ReadRow label="Registration" value={brand.registration_no} />
                <ReadRow label="VAT" value={brand.vat_no} />
                <ReadRow label="Brand voice" value={brand.brand_voice} />
                <ReadRow
                  label="What the company does"
                  value={(brand as any).business_description}
                />
                <div className="flex items-center gap-2 text-xs">
                  <span className="text-muted-foreground">Palette:</span>
                  <span
                    className="inline-block size-4 rounded border"
                    style={{ background: brand.primary_color }}
                    title={brand.primary_color}
                  />
                  <span
                    className="inline-block size-4 rounded border"
                    style={{ background: brand.secondary_color }}
                    title={brand.secondary_color}
                  />
                  <span className="text-muted-foreground">
                    {brand.primary_color} / {brand.secondary_color}
                  </span>
                </div>
              </div>
            </>
          )}
        </div>
      </div>

      {/* Generate AI document */}
      <div className="rounded-lg border bg-card p-4 space-y-3">
        <div className="flex items-center gap-2">
          <Sparkles className="size-5 text-amber-600" />
          <h2 className="font-semibold">Generate branded document with AI</h2>
        </div>
        <p className="text-xs text-muted-foreground">
          AI drafts a comprehensive, professionally designed document (minimum
          10 pages, except letterhead) with {brand.name}'s logo, colours and
          contact details applied to every page. Every document includes a
          clickable Table of Contents with PDF bookmarks.
        </p>
        <div className="grid sm:grid-cols-3 gap-3 items-end">
          <Field label="Document type">
            <Select value={genKind} onValueChange={setGenKind}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {DOC_KINDS.map((k) => (
                  <SelectItem key={k.value} value={k.value}>
                    {k.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Field>
          <Field label="Cover style">
            <Select value={genTemplate} onValueChange={setGenTemplate}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="auto">Auto (default per document)</SelectItem>
                <SelectItem value="royal">Royal — Burgundy &amp; Gold frame</SelectItem>
                <SelectItem value="legal">Legal — classic centred</SelectItem>
                <SelectItem value="magazine">Magazine — editorial</SelectItem>
                <SelectItem value="report">Report — split modern</SelectItem>
              </SelectContent>
            </Select>
          </Field>
          <Field label="Extra instructions (optional)">
            <Input
              value={genExtra}
              onChange={(e) => setGenExtra(e.target.value)}
              placeholder="e.g. emphasise solar industry, BBBEE Level 2…"
            />
          </Field>
        </div>
        {genTemplate === "royal" && (
          <p className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-md px-3 py-2">
            <strong>Royal cover</strong> — the full-bleed burgundy &amp; gold
            decorative frame will be used on the cover page of this document,
            with the client brand's logo, colours and metadata laid inside the
            cream centre panel. Ideal for Constitutions, Shareholder Agreements,
            Board Charters and other premium instruments issued for another
            company.
          </p>
        )}
        {(() => {
          const descLen = String(
            (brand as any).business_description ?? "",
          ).trim().length;
          const descOk = descLen >= MIN_BUSINESS_DESCRIPTION_CHARS;
          const needsDesc = genKind !== "letterhead" && !descOk;
          const bulkNeedsDesc = !descOk; // bulk pack always drafts body docs
          return (
            <>
              {(needsDesc || bulkNeedsDesc) && (
                <div className="rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-800">
                  <strong>"What the company does" is required.</strong> Add at
                  least {MIN_BUSINESS_DESCRIPTION_CHARS} characters describing
                  the company's activities, industries served, products/services
                  and any regulatory context (currently {descLen}). The AI
                  extracts activities, industries and service scope from this
                  field to tailor every section — generation is blocked until
                  it's provided.
                </div>
              )}
              <div className="flex flex-wrap justify-end gap-2">
                <Button
                  variant="outline"
                  onClick={() => setBulkSendOpen(true)}
                  disabled={bulkRunning}
                  title="Email selected documents to recipients with a weekly EFT instalment invoice"
                >
                  <Mail className="size-4 mr-2" />
                  Bulk send + invoice
                </Button>
                <Button
                  variant="outline"
                  onClick={() => {
                    if (
                      confirm(
                        `Generate the FULL STACK (${CLIENT_BRAND_BULK_PACK.length} documents) for ${brand.name}?\n\nThis runs every document in the catalogue and may take 10+ minutes. You can watch live progress below.`,
                      )
                    ) {
                      void runBulkPack();
                    }
                  }}
                  disabled={
                    bulkRunning || generateMut.isPending || bulkNeedsDesc
                  }
                  title={
                    bulkNeedsDesc
                      ? `Add at least ${MIN_BUSINESS_DESCRIPTION_CHARS} characters to "What the company does"`
                      : undefined
                  }
                >
                  {bulkRunning ? (
                    <Loader2 className="size-4 mr-2 animate-spin" />
                  ) : (
                    <Sparkles className="size-4 mr-2" />
                  )}
                  One-click: Generate Full Stack ({CLIENT_BRAND_BULK_PACK.length})

                </Button>
                <Button
                  variant="outline"
                  disabled={previewBusy || generateMut.isPending || bulkRunning || needsDesc}
                  title={
                    needsDesc
                      ? `Add at least ${MIN_BUSINESS_DESCRIPTION_CHARS} characters to "What the company does"`
                      : "Render + validate the Arial 10pt justified layout before saving"
                  }
                  onClick={async () => {
                    if (!brand) return;
                    setPreviewBusy(true);
                    try {
                      const r: any = await previewFn({
                        data: {
                          brand_id: brand.id,
                          kind: genKind as any,
                          extra_instructions: genExtra || undefined,
                          template_override:
                            genTemplate === "auto" ? undefined : (genTemplate as any),
                        },
                      });
                      setPreviewState({
                        pdf_base64: r.pdf_base64,
                        pages: r.pages,
                        layout_report: r.layout_report,
                        kind: genKind,
                      });
                    } catch (e: any) {
                      toast.error(e?.message ?? "Preview failed");
                    } finally {
                      setPreviewBusy(false);
                    }
                  }}
                >
                  {previewBusy && <Loader2 className="size-4 mr-2 animate-spin" />}
                  Preview & validate
                </Button>
                <Button
                  onClick={() => generateMut.mutate({})}
                  disabled={
                    generateMut.isPending || bulkRunning || needsDesc || previewBusy
                  }
                  title={
                    needsDesc
                      ? `Add at least ${MIN_BUSINESS_DESCRIPTION_CHARS} characters to "What the company does"`
                      : undefined
                  }
                >
                  {generateMut.isPending && (
                    <Loader2 className="size-4 mr-2 animate-spin" />
                  )}
                  Generate document
                </Button>
              </div>
            </>
          );
        })()}
      </div>

      {/* Bulk progress panel */}
      {bulkItems && (
        <BulkProgressPanel
          items={bulkItems}
          running={bulkRunning}
          onRetry={(i) => retryBulkItem(i)}
          onDismiss={() => setBulkItems(null)}
        />
      )}
      {bulkSendOpen && (
        <BrandBulkSendDialog
          brandId={brand.id}
          brandName={brand.name}
          onClose={() => setBulkSendOpen(false)}
        />
      )}


      {/* Generation status panel */}
      {lastGen && (
        <GenerationStatusPanel
          state={lastGen}
          onRetry={() => generateMut.mutate({ retryRequestId: lastGen.requestId })}
          onDismiss={() => setLastGen(null)}
          isPending={generateMut.isPending}
        />
      )}

      {/* Preview & layout audit dialog */}
      {previewState && (
        <BrandPreviewDialog
          state={previewState}
          onClose={() => setPreviewState(null)}
          onSave={() => {
            setPreviewState(null);
            generateMut.mutate({});
          }}
          savingDisabled={generateMut.isPending}
        />
      )}







      {/* Bulk upload + AI context extraction */}
      <div className="rounded-lg border bg-card p-4 space-y-3">
        <div className="flex items-center gap-2">
          <Upload className="size-5 text-emerald-600" />
          <h2 className="font-semibold">Bulk upload &amp; AI context extraction</h2>
        </div>
        <p className="text-sm text-muted-foreground">
          Upload multiple reference files (CIPC docs, existing profiles, prior contracts, marketing
          PDFs). Our AI reads every file, blends it with our own service catalog, and produces
          precise activities, industries, service scope, regulatory context and a polished business
          description used by every generator.
        </p>
        <input
          ref={bulkInputRef}
          type="file"
          multiple
          className="hidden"
          accept=".pdf,.txt,.doc,.docx,.png,.jpg,.jpeg,.webp,application/pdf,text/*,image/*"
          onChange={(e) => handleBulkContextUpload(e.target.files)}
        />
        <div className="flex flex-wrap gap-2 items-center">
          <Button
            type="button"
            onClick={() => bulkInputRef.current?.click()}
            disabled={bulkUploading || extracting}
          >
            {bulkUploading ? "Uploading…" : extracting ? "AI extracting…" : "Upload files & extract"}
          </Button>
          {bulkUploadMsg && (
            <span className="text-sm text-muted-foreground">{bulkUploadMsg}</span>
          )}
        </div>
        {extractionResult && (
          <div className="rounded-md border bg-muted/40 p-3 text-sm space-y-2">
            <div className="font-medium">Extraction summary</div>
            {extractionResult.business_description && (
              <div>
                <div className="text-xs uppercase text-muted-foreground">Business description</div>
                <div className="whitespace-pre-wrap">{extractionResult.business_description}</div>
              </div>
            )}
            {Array.isArray(extractionResult.recommended_services) &&
              extractionResult.recommended_services.length > 0 && (
                <div>
                  <div className="text-xs uppercase text-muted-foreground">
                    Recommended services from our catalog
                  </div>
                  <ul className="list-disc pl-5">
                    {extractionResult.recommended_services.map((s: string) => (
                      <li key={s}>{s}</li>
                    ))}
                  </ul>
                </div>
              )}
          </div>
        )}
      </div>


      {/* Upload reference doc */}
      <div className="rounded-lg border bg-card p-4 space-y-3">
        <div className="flex items-center gap-2">
          <Upload className="size-5 text-amber-600" />
          <h2 className="font-semibold">Upload reference / existing document</h2>
        </div>
        <div className="grid sm:grid-cols-3 gap-3 items-end">
          <Field label="Category">
            <Select value={uploadKind} onValueChange={setUploadKind}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {UPLOAD_KINDS.map((k) => (
                  <SelectItem key={k.value} value={k.value}>
                    {k.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Field>
          <Field label="Title" className="sm:col-span-2">
            <Input
              value={uploadTitle}
              onChange={(e) => setUploadTitle(e.target.value)}
              placeholder="e.g. 2024 CIPC registration certificate"
            />
          </Field>
        </div>
        <label className="block">
          <input
            type="file"
            className="hidden"
            accept=".pdf,.doc,.docx,.xls,.xlsx,.png,.jpg,.jpeg,.webp,.txt,application/pdf,image/*"
            onChange={(e) => {
              const f = e.target.files?.[0];
              if (f) handleDocUpload(f);
              e.target.value = "";
            }}
          />
          <Button asChild variant="outline" disabled={busy === "doc"}>
            <span>
              {busy === "doc" ? (
                <Loader2 className="size-4 mr-2 animate-spin" />
              ) : (
                <Upload className="size-4 mr-2" />
              )}
              Choose file (≤ 25MB, PDF / DOC / DOCX / XLS / image / TXT)
            </span>
          </Button>
        </label>
        <p className="text-[11px] text-muted-foreground">
          Allowed types: PDF, DOC, DOCX, XLS, XLSX, PNG, JPG, WebP, TXT. Max {bytesPretty(DOC_MAX_BYTES)}.
        </p>
      </div>

      {/* Documents gallery */}
      <DocumentGallery
        documents={data?.documents ?? []}
        kindFilter={galleryKind}
        onKindFilter={setGalleryKind}
        onOpen={(p, n) => openFile(p, n)}
        onDelete={(docId) => {
          if (confirm("Delete this document?")) deleteDocMut.mutate(docId);
        }}
      />
    </div>
  );
}

function GenerationStatusPanel({
  state,
  onRetry,
  onDismiss,
  isPending,
}: {
  state: GenState;
  onRetry: () => void;
  onDismiss: () => void;
  isPending: boolean;
}) {
  const ringColor =
    state.status === "succeeded"
      ? "border-emerald-300 bg-emerald-50/60"
      : state.status === "failed"
        ? "border-rose-300 bg-rose-50/60"
        : "border-amber-300 bg-amber-50/60";
  const title =
    state.status === "running"
      ? "Generation in progress…"
      : state.status === "succeeded"
        ? `Generated successfully (${state.pages ?? "?"} pages)`
        : "Generation failed";
  return (
    <div className={`rounded-lg border p-4 space-y-3 ${ringColor}`}>
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          {state.status === "running" ? (
            <Loader2 className="size-4 animate-spin" />
          ) : (
            <Sparkles className="size-4" />
          )}
          <div>
            <div className="font-semibold text-sm">{title}</div>
            <div className="text-xs text-muted-foreground">
              {DOC_KIND_LABEL[state.kind] ?? state.kind} · request{" "}
              <code className="text-[10px]">{state.requestId.slice(0, 18)}…</code>
            </div>
          </div>
        </div>
        <div className="flex gap-2">
          {state.status === "failed" && (
            <Button size="sm" onClick={onRetry} disabled={isPending}>
              {isPending && <Loader2 className="size-4 mr-2 animate-spin" />}
              Retry safely
            </Button>
          )}
          <Button size="sm" variant="ghost" onClick={onDismiss}>
            Dismiss
          </Button>
        </div>
      </div>
      {state.error && (
        <div className="text-xs text-rose-700 bg-rose-100/60 border border-rose-200 rounded p-2">
          {state.error}
        </div>
      )}
      <details className="text-xs">
        <summary className="cursor-pointer text-muted-foreground">
          Generation log ({state.logs.length})
        </summary>
        <ol className="mt-2 space-y-1 max-h-56 overflow-auto rounded border bg-background p-2">
          {state.logs.map((l, i) => (
            <li key={i} className="flex gap-2">
              <span
                className={`text-[10px] uppercase font-mono shrink-0 w-12 ${
                  l.status === "error"
                    ? "text-rose-600"
                    : l.status === "warn"
                      ? "text-amber-600"
                      : l.status === "ok"
                        ? "text-emerald-600"
                        : "text-muted-foreground"
                }`}
              >
                {l.status}
              </span>
              <span className="font-mono text-[10px] text-muted-foreground shrink-0 w-20">
                {l.step}
              </span>
              <span className="flex-1">{l.message}</span>
            </li>
          ))}
        </ol>
      </details>
    </div>
  );
}

function DocumentGallery({
  documents,
  kindFilter,
  onKindFilter,
  onOpen,
  onDelete,
}: {
  documents: any[];
  kindFilter: string;
  onKindFilter: (v: string) => void;
  onOpen: (path: string, name?: string) => void;
  onDelete: (id: string) => void;
}) {
  const kinds = Array.from(new Set(documents.map((d) => d.kind)));
  const filtered =
    kindFilter === "all" ? documents : documents.filter((d) => d.kind === kindFilter);

  return (
    <div className="rounded-lg border bg-card">
      <div className="p-4 border-b flex items-center gap-2 flex-wrap">
        <FileText className="size-5" />
        <h2 className="font-semibold">Document gallery</h2>
        <span className="text-xs text-muted-foreground">
          ({documents.length})
        </span>
        {kinds.length > 0 && (
          <div className="ml-auto flex items-center gap-2">
            <Label className="text-xs text-muted-foreground">Filter</Label>
            <Select value={kindFilter} onValueChange={onKindFilter}>
              <SelectTrigger className="h-8 w-[220px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All documents</SelectItem>
                {kinds.map((k) => (
                  <SelectItem key={k} value={k}>
                    {DOC_KIND_LABEL[k] ?? k}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}
      </div>
      {filtered.length === 0 ? (
        <div className="p-6 text-center text-muted-foreground text-sm">
          No documents in this view yet.
        </div>
      ) : (
        <ul className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3 p-4">
          {filtered.map((d) => {
            const isPdf =
              (d.mime_type ?? "").includes("pdf") ||
              (d.original_filename ?? "").toLowerCase().endsWith(".pdf");
            const isImage = (d.mime_type ?? "").startsWith("image/");
            const failed = d.status === "failed";
            return (
              <li
                key={d.id}
                className={`rounded-lg border bg-background overflow-hidden flex flex-col ${
                  failed ? "border-rose-300" : ""
                }`}
              >
                <div className="aspect-[4/3] bg-muted/40 flex items-center justify-center relative">
                  {isPdf ? (
                    <div className="flex flex-col items-center text-muted-foreground">
                      <FileText className="size-12 text-rose-500" />
                      <span className="text-[10px] uppercase tracking-wider mt-1">
                        PDF · {d.page_count ?? "—"} pages
                      </span>
                    </div>
                  ) : isImage ? (
                    <ImageIcon className="size-12 text-muted-foreground" />
                  ) : (
                    <FileText className="size-12 text-muted-foreground" />
                  )}
                  {d.ai_generated && (
                    <span className="absolute top-1 left-1 text-[10px] rounded bg-amber-100 text-amber-900 px-1.5 py-0.5">
                      AI
                    </span>
                  )}
                  {failed && (
                    <span className="absolute top-1 right-1 text-[10px] rounded bg-rose-100 text-rose-900 px-1.5 py-0.5">
                      FAILED
                    </span>
                  )}
                </div>
                <div className="p-3 space-y-1 flex-1 flex flex-col">
                  <div className="text-sm font-medium truncate" title={d.title}>
                    {d.title}
                  </div>
                  <div className="text-[11px] text-muted-foreground flex flex-wrap gap-1">
                    <span className="rounded bg-muted px-1.5 py-0.5">
                      {DOC_KIND_LABEL[d.kind] ?? d.kind}
                    </span>
                    <span>{bytesPretty(d.size_bytes ?? 0)}</span>
                    <span>{new Date(d.created_at).toLocaleDateString()}</span>
                  </div>
                  {d.error_message && (
                    <div className="text-[11px] text-rose-700">
                      {d.error_message}
                    </div>
                  )}
                  <div className="mt-auto pt-2 flex gap-2">
                    {!failed && d.storage_path && (
                      <Button
                        size="sm"
                        variant="outline"
                        className="flex-1"
                        onClick={() => onOpen(d.storage_path, d.original_filename)}
                      >
                        <Download className="size-4 mr-1" /> Download
                      </Button>
                    )}
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => onDelete(d.id)}
                    >
                      <Trash2 className="size-4" />
                    </Button>
                  </div>
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

function ReadRow({ label, value }: { label: string; value: any }) {
  if (!value) return null;
  return (
    <div className="flex gap-2">
      <span className="text-muted-foreground w-24 shrink-0">{label}:</span>
      <span className="truncate">{value}</span>
    </div>
  );
}

function BulkProgressPanel({
  items,
  running,
  onRetry,
  onDismiss,
}: {
  items: BulkItem[];
  running: boolean;
  onRetry: (index: number) => void;
  onDismiss: () => void;
}) {
  const done = items.filter((i) => i.status === "succeeded" || i.status === "failed").length;
  const ok = items.filter((i) => i.status === "succeeded").length;
  const fail = items.filter((i) => i.status === "failed").length;
  const pct = Math.round((done / Math.max(1, items.length)) * 100);
  return (
    <div className="rounded-lg border bg-card p-4 space-y-3">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          {running ? (
            <Loader2 className="size-4 animate-spin text-amber-600" />
          ) : (
            <Sparkles className="size-4 text-emerald-600" />
          )}
          <div>
            <div className="font-semibold text-sm">
              {running ? "Generating full pack…" : "Bulk generation complete"}
            </div>
            <div className="text-xs text-muted-foreground">
              {done} / {items.length} done · {ok} succeeded · {fail} failed
            </div>
          </div>
        </div>
        {!running && (
          <Button size="sm" variant="ghost" onClick={onDismiss}>
            Dismiss
          </Button>
        )}
      </div>
      <div className="h-2 w-full rounded bg-muted overflow-hidden">
        <div
          className="h-full bg-amber-500 transition-all"
          style={{ width: `${pct}%` }}
        />
      </div>
      <ul className="divide-y rounded border bg-background">
        {items.map((it, i) => (
          <li key={it.kind} className="flex items-center gap-2 p-2 text-xs">
            <span className="w-5 text-center">
              {it.status === "queued" && <span className="text-muted-foreground">·</span>}
              {it.status === "running" && (
                <Loader2 className="size-3 animate-spin text-amber-600 mx-auto" />
              )}
              {it.status === "succeeded" && <span className="text-emerald-600">✓</span>}
              {it.status === "failed" && <span className="text-rose-600">✕</span>}
            </span>
            <span className="flex-1 truncate font-medium">
              {DOC_KIND_LABEL[it.kind] ?? it.kind}
            </span>
            {it.status === "succeeded" && (
              <span className="text-muted-foreground">{it.pages} pp</span>
            )}
            {it.status === "failed" && (
              <>
                <span
                  className="text-rose-700 truncate max-w-[40%]"
                  title={it.error}
                >
                  {it.error}
                </span>
                <Button
                  size="sm"
                  variant="outline"
                  className="h-6 px-2 text-[11px]"
                  disabled={running}
                  onClick={() => onRetry(i)}
                >
                  Retry
                </Button>
              </>
            )}
          </li>
        ))}
      </ul>
      {!running && fail > 0 && (
        <p className="text-[11px] text-muted-foreground">
          Failed documents did not block the rest of the pack. Use the per-row Retry button to re-run any failed item.
        </p>
      )}
    </div>
  );
}

function BrandPreviewDialog(props: {
  state: {
    pdf_base64: string;
    pages: number;
    layout_report: {
      ok: boolean;
      layout: { font_family: string; font_size_pt: number; alignment: string };
      section_count: number;
      estimated_pages: number;
      warnings: { section: string; kind: string; detail: string }[];
    };
    kind: string;
  };
  onClose: () => void;
  onSave: () => void;
  savingDisabled: boolean;
}) {
  const { state, onClose, onSave, savingDisabled } = props;
  const src = `data:application/pdf;base64,${state.pdf_base64}`;
  const { warnings, layout } = state.layout_report;
  const hasWarnings = warnings.length > 0;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4">
      <div className="w-full max-w-6xl h-[92vh] rounded-lg bg-background shadow-xl flex flex-col overflow-hidden">
        <div className="flex items-center justify-between border-b p-3">
          <div>
            <div className="font-semibold">
              Preview & validate — {state.kind}
            </div>
            <div className="text-xs text-muted-foreground">
              {state.pages} page{state.pages === 1 ? "" : "s"} rendered ·{" "}
              {layout.font_family} {layout.font_size_pt}pt · {layout.alignment}
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="ghost" size="sm" onClick={onClose}>
              Discard
            </Button>
            <Button
              size="sm"
              onClick={onSave}
              disabled={savingDisabled}
              title={
                hasWarnings
                  ? "Warnings present — save anyway if the layout is acceptable"
                  : "Layout audit clean"
              }
            >
              {hasWarnings ? "Save anyway" : "Save to gallery"}
            </Button>
          </div>
        </div>
        <div className="grid flex-1 min-h-0 grid-cols-1 lg:grid-cols-[1fr_320px]">
          <iframe
            title="Brand PDF preview"
            src={src}
            className="w-full h-full bg-muted"
          />
          <div className="border-l overflow-auto p-3 space-y-3 text-sm">
            <div
              className={`rounded-md border p-3 ${hasWarnings ? "border-amber-300 bg-amber-50" : "border-emerald-300 bg-emerald-50"}`}
            >
              <div className="font-medium">
                {hasWarnings
                  ? `${warnings.length} layout warning${warnings.length === 1 ? "" : "s"}`
                  : "Layout audit passed"}
              </div>
              <ul className="mt-1 text-xs list-disc pl-4 space-y-1 text-muted-foreground">
                <li>
                  Typography: {layout.font_family} {layout.font_size_pt}pt
                </li>
                <li>Alignment: {layout.alignment}</li>
                <li>Sections: {state.layout_report.section_count}</li>
                <li>Estimated pages: {state.layout_report.estimated_pages}</li>
                <li>Rendered pages: {state.pages}</li>
              </ul>
            </div>
            {hasWarnings && (
              <ul className="space-y-2">
                {warnings.map((w, i) => (
                  <li
                    key={i}
                    className="rounded border border-amber-200 bg-amber-50/50 p-2 text-xs"
                  >
                    <div className="font-medium">
                      {w.kind.replace(/_/g, " ")} · {w.section}
                    </div>
                    <div className="text-muted-foreground">{w.detail}</div>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

